import { forwardRef, useEffect, useRef, type ForwardRefExoticComponent, type RefAttributes } from "react";
import { Button, Icon, cn } from "@excellent/ui";
import { useStage, type AppTab } from "./stage-context";
import { PlayStagePane } from "./play-stage-pane";
import { ChatStagePane } from "./chat-stage-pane";

export function StageHost() {
  const { open, tabs, activeSlug, minimize, closeTab, setActive } = useStage();
  const headerRef = useRef<HTMLDivElement>(null);
  const active = tabs.find((tab) => tab.slug === activeSlug) ?? tabs[0] ?? null;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      minimize();
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, minimize]);

  useEffect(() => {
    if (!open) return;
    headerRef.current?.focus();
  }, [open, activeSlug]);

  if (tabs.length === 0 && !open) return null;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-[59] bg-black/40 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        aria-hidden
        onClick={minimize}
      />
      <div
        role="dialog"
        aria-modal={open ? "true" : undefined}
        aria-label="Stage"
        aria-hidden={!open}
        className={cn(
          "fixed inset-[10px] z-[60] flex flex-col overflow-hidden rounded-xl border border-border bg-popover shadow-surface-6",
          open ? "" : "pointer-events-none invisible",
        )}
      >
        <StageHeader
          ref={headerRef}
          tabs={tabs}
          activeSlug={active?.slug ?? null}
          onSelect={setActive}
          onClose={closeTab}
          onMinimize={minimize}
        />
        <div className="relative min-h-0 flex-1 bg-background">
          {tabs.map((tab) => (
            <TabContent
              key={tab.slug}
              tab={tab}
              active={tab.slug === active?.slug}
              onClose={closeTab}
            />
          ))}
          {tabs.length === 0 ? (
            <div className="absolute inset-0 grid place-items-center type-body text-muted-foreground">
              No stage items open.
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

const StageHeader: ForwardRefExoticComponent<
  StageHeaderProps & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, StageHeaderProps>(function StageHeader(
  { tabs, activeSlug, onSelect, onClose, onMinimize },
  ref,
) {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="flex h-9 shrink-0 items-center gap-1 border-b border-border/60 bg-muted/40 pl-1.5 pr-1.5 outline-none"
    >
      <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
        {tabs.map((tab) => (
          <StageTab
            key={tab.slug}
            tab={tab}
            active={tab.slug === activeSlug}
            onSelect={() => onSelect(tab.slug)}
            onClose={() => onClose(tab.slug)}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-1 shrink-0"
        onClick={onMinimize}
        title="Back to Excellent (Esc)"
        aria-label="Back to Excellent"
      >
        <Icon name="chevron-down" className="size-3.5" />
        Excellent
      </Button>
    </div>
  );
});

interface StageHeaderProps {
  tabs: AppTab[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  onClose: (slug: string) => void;
  onMinimize: () => void;
}

function StageTab({
  tab,
  active,
  onSelect,
  onClose,
}: {
  tab: AppTab;
  active: boolean;
  onSelect: () => void;
  onClose: () => void;
}) {
  return (
    <div
      role="tab"
      aria-selected={active}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      title={tab.name}
      className={cn(
        "group/stagetab relative flex h-7 max-w-[12rem] shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2 type-subtext transition-colors",
        active
          ? "bg-background text-foreground"
          : "text-muted-foreground hover:bg-background/50 hover:text-foreground",
      )}
    >
      <Icon
        name={tab.kind === "chat" ? "message-square" : "play"}
        className="size-3.5 shrink-0"
      />
      <span className="truncate">{tab.name}</span>
      <span
        role="button"
        tabIndex={-1}
        aria-label={`Close ${tab.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className={cn(
          "grid size-4 place-items-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground",
          active ? "opacity-100" : "opacity-0 group-hover/stagetab:opacity-100",
        )}
      >
        <Icon name="x" className="size-3" />
      </span>
    </div>
  );
}

function TabContent({
  tab,
  active,
  onClose,
}: {
  tab: AppTab;
  active: boolean;
  onClose: (slug: string) => void;
}) {
  return (
    <section
      data-stage-kind={tab.kind}
      data-stage-slug={tab.slug}
      className={cn("absolute inset-0 min-h-0", !active && "hidden")}
    >
      {tab.kind === "play" ? (
        <PlayStagePane
          startActionKey={tab.startActionKey}
          active={active}
          onExhausted={() => onClose(tab.slug)}
        />
      ) : (
        <ChatStagePane active={active} onExhausted={() => onClose(tab.slug)} />
      )}
    </section>
  );
}
